import {
  attributeDisplayLabel,
  attributeValueDisplayLabel,
} from '@/shein-platform/utils/sheinAttributeTemplate';
import { skcHasColor } from '@/shein-platform/modules/products/components/SpecSupplySection';
import { resolvePublishSlotImageUrl } from '@/shein-platform/utils/sheinPublishImageSlots';
import { appendCopywritingMaxCharsToPrompt } from '@/pages/ipe/workbench/constants/aiCopywritingConfig';
import { resolveImg2ImgReferenceForApi } from '@/pages/ipe/workbench/utils/img2imgPublicUrl';
import { aiCopywritingAPI } from '@/utils/api';

export const SHEIN_PUBLISH_TITLE_MAX_CHARS = 150;

const BASE_PROMPT =
  '根据商品主图，结合以下类目与商品属性，生成一条英文电商平台产品标题。要求：全英文、简洁有吸引力、突出卖点、不含中文、不超过150字符。';

function normalizeGeneratedTitle(raw) {
  const text = String(raw || '').trim();
  return text.replace(/^(?:商品标题|产品标题|标题)\s*[:：]\s*/i, '').trim();
}

function findAttributeValueName(attr, valueId) {
  const id = String(valueId || '').trim();
  if (!id) return '';
  const values = Array.isArray(attr?.values) ? attr.values : [];
  const hit = values.find((v) => String(v?.id) === id);
  return hit ? attributeValueDisplayLabel(hit) : '';
}

function resolveStoredAttributeValueText(attr, raw) {
  if (Array.isArray(raw)) {
    const parts = raw.map((row) => {
      if (row && typeof row === 'object') {
        const vid = String(row.valueId ?? row.attributeValueId ?? '').trim();
        const pct = String(row.percent ?? row.attributeExtraValue ?? '').trim();
        const name = findAttributeValueName(attr, vid) || vid;
        return pct ? `${name} ${pct}%` : name;
      }
      return findAttributeValueName(attr, row) || String(row || '').trim();
    }).filter(Boolean);
    return parts.join(', ');
  }
  if (raw && typeof raw === 'object') {
    const vid = String(raw.valueId ?? raw.attributeValueId ?? '').trim();
    const extra = String(raw.attributeExtraValue ?? raw.percent ?? '').trim();
    if (vid) {
      const name = findAttributeValueName(attr, vid) || vid;
      return extra ? `${name} ${extra}` : name;
    }
    return extra;
  }
  const text = String(raw ?? '').trim();
  return findAttributeValueName(attr, text) || text;
}

export function formatFilledProductAttributeLines(productAttributes = {}, attrDefs = []) {
  const attrs = Array.isArray(attrDefs) ? attrDefs : [];
  const lines = [];
  attrs.forEach((attr) => {
    const id = String(attr?.attributeId || '').trim();
    if (!id) return;
    const raw = productAttributes?.[id];
    if (raw == null || raw === '') return;
    const label = attributeDisplayLabel(attr) || id;
    const valueText = resolveStoredAttributeValueText(attr, raw);
    if (valueText) lines.push(`${label}: ${valueText}`);
  });
  return lines;
}

export function buildSheinPublishTitlePrompt({
  categoryPathLabel = '',
  productAttributes = {},
  attrDefs = [],
} = {}) {
  const lines = [];
  const category = String(categoryPathLabel || '').trim();
  if (category) lines.push(`类目：${category}`);
  const attrLines = formatFilledProductAttributeLines(productAttributes, attrDefs);
  if (attrLines.length) {
    lines.push('商品属性：');
    attrLines.forEach((line) => lines.push(`- ${line}`));
  }
  const context = lines.join('\n');
  const body = context ? `${BASE_PROMPT}\n\n${context}` : BASE_PROMPT;
  return appendCopywritingMaxCharsToPrompt(body, SHEIN_PUBLISH_TITLE_MAX_CHARS, 500);
}

/** 第一个已选主规格颜色的主图 URL */
export function resolveFirstSkcMainImageUrl(skcList = []) {
  const list = Array.isArray(skcList) ? skcList : [];
  for (const skc of list) {
    if (!skcHasColor(skc)) continue;
    const url = resolvePublishSlotImageUrl(skc.mainImage);
    if (url) return url;
  }
  return '';
}

/**
 * 调用 AI 文案计费接口生成标题（按张扣 IPE 豆，与工作室 AI 文案同价）。
 */
export async function generateSheinPublishAiTitle({
  skcList = [],
  categoryPathLabel = '',
  productAttributes = {},
  attrDefs = [],
} = {}) {
  const imageUrl = resolveFirstSkcMainImageUrl(skcList);
  if (!imageUrl) {
    throw new Error('请先为第一个颜色上传主图');
  }

  const publicRef = await resolveImg2ImgReferenceForApi(imageUrl);
  if (!publicRef) {
    throw new Error('主图无法解析，请重新上传');
  }

  const prompt = buildSheinPublishTitlePrompt({
    categoryPathLabel,
    productAttributes,
    attrDefs,
  });

  const result = await aiCopywritingAPI.submitAndWait({
    requestId: `shein-publish-title-${Date.now()}`,
    prompt,
    referenceUrls: [publicRef],
    outputCount: 1,
    language: 'en',
    maxChars: SHEIN_PUBLISH_TITLE_MAX_CHARS,
  });

  const title = normalizeGeneratedTitle(result?.content || result?.text || '');
  if (!title) throw new Error('AI 未返回标题');

  return title.slice(0, SHEIN_PUBLISH_TITLE_MAX_CHARS);
}
